// Dependencies
import React, { useState } from 'react';

import _ from 'lodash';
import { connect } from 'react-redux';
import { Impression } from '@nerdwallet/analytics/react/impression';
import {
  AnalyticsPageView,
  AnalyticsProvider,
} from '@nerdwallet/seo-analytics';
import { getMacroKey } from '@nerdwallet/react-config-builder';
import { ENTITY_NAME, SECTION_NAME } from '../../lib/analytics-constants';
import lazy, { Hydrate } from '../../components/lazy/lazy-universal';

// Actions
// Local Components
import Nav from '../../components/currency/nav/nav';
import Results from '../../components/currency/results/results';
import ToolLinkCards from '../../components/currency/tool-link-cards/tool-link-cards';
import ResultsPanel from '../../components/home-affordability-calculator/results-panel/results-panel';
import OnboardingModal from '../../components/home-affordability-calculator/onboarding-modal';

// Configs and Data
import { RELATED_ARTICLES } from '../../components/home-affordability-calculator/analytics-constants';
import DTIDetailsConfig from '../../components/home-affordability-calculator/dti-details-view-config';
import navConfig from '../../components/home-affordability-calculator/fthb-nav-config';
import HACControlPanel from '../../components/home-affordability-calculator/hac-control-panel/hac-control-panel';
import inputLibrary from '../../components/home-affordability-calculator/input-library';
import toolLinkConfig from '../../components/home-affordability-calculator/tool-link-config';

import { MarketplaceRateTable } from '../../components/marketplace-rate-table';
import { useUrlRecordContext } from '../../components/url-record';
import { overridesChange } from '../../lib/actions';
import inputChange from '../../lib/actions/inputChange';
import { HomeAffordabilityInput } from '../../lib/utils/inputs';
import type { Browser, HomeAffordabilityReducer } from '../../types';
import SegmentImpression from '../../lib/analytics/segment-impression';

// Lazy Load Components
const LenderCards = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-lender-cards' */ '../../components/currency/lender-cards/lender-cards'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const RelatedArticles = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-related-articles' */ '../../components/currency/related-articles/related-articles'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const Methodology = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-methodology' */ '../../components/currency/methodology/methodology'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const HlmEntry = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-hlm-entry' */ '../../components/hlm-entry'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

export interface HACProps
  extends Pick<HomeAffordabilityInput, 'manualInputs' | 'validations'> {
  onboardingBypass: boolean;
  dispatch: (a: Record<string, any>) => void; // TODO: UPDATE!
  results: HomeAffordabilityReducer;
  app?: string;
  browser?: Browser;
  location?: Record<string, any>;
  history?: Record<string, any>;
  routeName?: string;
}

const HACResults = (props: HACProps): JSX.Element => {
  const {
    onboardingBypass,
    dispatch,
    app,
    browser,
    location,
    results,
    validations,
    routeName,
  } = props;
  const [showOnboarding, setShowOnboarding] = useState(!onboardingBypass);

  const { title, synopsis } = useUrlRecordContext();
  const onChange = (config, value) => {
    const key = getMacroKey(config, 'value');

    const data = _.set(
      {
        name: 'results',
      },
      key,
      value
    );

    // Apply the right action based on what the key is.
    const allInputs = config.additionalInputIdsToValidate
      ? config.additionalInputIdsToValidate
          .map((input) => inputLibrary[input])
          .concat(inputLibrary[config.inputId])
      : [inputLibrary[config.inputId]];

    if (_.has(data, 'overrides')) {
      dispatch(overridesChange(data, _.compact(allInputs)));
    } else {
      dispatch(inputChange(data, _.compact(allInputs)));
    }
  };

  const closeModal = () => {
    setShowOnboarding(false);
  };

  const data = {
    ...results,
    validations,
  };

  const controlPanelProps = {
    app,
    enableTracking: true,
    onChange,
    browser,
    data,
    DTIDetailsConfig,
  };

  const resultsPanelProps = {
    browser,
    data,
    dispatch,
    onChange,
    DTIDetailsConfig,
  };

  return (
    <AnalyticsProvider>
      <AnalyticsPageView
        name={ENTITY_NAME.MORTGAGES_HOME_AFFORDABILITY_CALCULATOR}
      >
        <Impression
          eventType="page_impression"
          entityName={
            routeName === 'default'
              ? 'mortgages_home_affordability_calculator_form'
              : 'mortgages_home_affordability_calculator'
          }
        >
          <SegmentImpression
            eventType="form_viewed"
            attributes={{
              entity_name: 'how_much_house_can_i_afford_calculator',
              form_name: 'how_much_house_can_i_afford_calculator',
            }}
          >
            <OnboardingModal
              isOpen={showOnboarding}
              browser={browser}
              location={location}
              onClose={closeModal}
            />
            <Results
              {..._.assign({}, props, {
                fthbNav: <Nav {...navConfig} app="hac" />,
                controlPanel: (
                  <HACControlPanel enableTracking {...controlPanelProps} />
                ),
                data,
                heading: title,
                subcopy: synopsis,
                membershipCta: <HlmEntry />,
                resultsPanel: <ResultsPanel {...resultsPanelProps} />,
                summaryTable: <MarketplaceRateTable />,
                lenders: (
                  <LenderCards
                    trackingFormName="hac"
                    explanation="Confirm your affordability with a lender."
                    namedQuery="mortgages_offers_hac"
                    analyticsTrackingConfig={{
                      entityName: 'mortgages_home_affordability_offer',
                      sectionName: 'mortgages_home_affordability_offer',
                      sectionPosition: 1,
                    }}
                    cardsPerRow={2}
                  />
                ),
                methodology: (
                  <Methodology
                    trackingFormName="hac-methodology"
                    impressionTracking
                  />
                ),
                relatedArticles: (
                  <RelatedArticles
                    entityName={RELATED_ARTICLES.entityName}
                    sectionName={RELATED_ARTICLES.sectionName}
                    sectionPosition={RELATED_ARTICLES.sectionPosition}
                    articles={RELATED_ARTICLES.articles}
                  />
                ),
                toolLinkCards: (
                  <ToolLinkCards
                    header="Other NerdWallet resources"
                    cards={toolLinkConfig.cards}
                    impressionTrackingName={
                      SECTION_NAME.MORTGAGES_HOME_AFFORDABILITY_TOOL_CARD
                    }
                    analyticsConfig={{
                      sectionName:
                        SECTION_NAME.MORTGAGES_HOME_AFFORDABILITY_TOOL_CARD,
                      sectionPosition: 6,
                    }}
                    data={data}
                  />
                ),
              })}
            />
          </SegmentImpression>
        </Impression>
      </AnalyticsPageView>
    </AnalyticsProvider>
  );
};

const mapStateToProps = (state, ownProps) => ({
  ...state,
  ownProps,
  routName: ownProps?.route?.name,
});

export default connect(mapStateToProps)(HACResults);
